import React, { useEffect, useState, useMemo } from "react";
import { getApi } from "../../../Services/apiService";
import {
  getMemberDeathYear,
  getMemberTimeSinceDeathLabel,
} from "../../../Utils/family-tree/dateAndTime";
import { getDashboardMemorialMonthDay } from "../../../Utils/dashboardPeriodMonth";
import { formatMsToMMDDYYYY } from "../../../Utils/dateUtils";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
  isDashboardDescendantSpouseDeceased,
  isDashboardInLawMember,
  isDashboardUpcomingDeceasedMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardPeriodPickers, {
  DASHBOARD_PERIOD_ALL_MONTHS as ALL_MONTHS,
  DASHBOARD_PERIOD_MONTH_NAMES,
} from "./DashboardPeriodPickers";
import {
  applyDashboardMemberFilterToggle,
  filterDashboardMembersByMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { sortDashboardMembersByDeathDate } from "../../../Utils/dashboardFamilyLineNumbers";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

const MONTH_NAMES = DASHBOARD_PERIOD_MONTH_NAMES;

/** The rows that fall in `monthIndex` (0–11). */
const filterMemorialsByMonth = (rows, monthIndex) =>
  rows.filter((row) => getDashboardMemorialMonthDay(row)?.month === monthIndex);

/**
 * A month's rows: the payload's own rows for it first, then any deceased
 * member of that month off the roster the payload left out, so a month the
 * endpoint does not cover still reads off the tree.
 */
function mergeMonthMemorials(payloadRows, rosterRows) {
  const seenIds = new Set(
    payloadRows.filter((row) => row?.id != null).map((row) => String(row.id))
  );
  const extras = rosterRows.filter(
    (row) => row?.id == null || !seenIds.has(String(row.id))
  );
  return [...payloadRows, ...extras];
}

const isInLawMember = (member) => {
  return (
    isDashboardInLawMember(member) &&
    Boolean(member?.head_first_name)
  );
};

function MemorialsThisWeek() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  // The full roster: the colour legend is built from it, and so is every month
  // other than the current one — see periodMonths.
  const [rosterMembers, setRosterMembers] = useState([]);
  // The payload's own memorials rows — every month it names, not only the
  // current one; see periodMonths.
  const [payloadMemorials, setPayloadMemorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  // 1–12, or ALL_MONTHS for the whole year.
  const [month, setMonth] = useState(now.getMonth() + 1);
  // date-asc/desc | year-asc/desc | oldest/youngest (death year) | alpha-asc/desc | color | death-asc | none
  const [sortBy, setSortBy] = useState("date-asc");
  const [memberFilter, setMemberFilter] = useState("all");
  const [isLargeView, setIsLargeView] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // A member tab on the right of the bar's divider is selected on its own: it
  // clears every sort on the left, Date included. The list stays grouped by
  // calendar day — the sort branch every unsorted list falls through to — so the
  // order holds with no sort button lit for it.
  //
  // The sort side effect runs here in the click handler, not inside a
  // setMemberFilter updater: updaters run in the render phase, where React does not
  // reliably apply a setState for another piece of state — the list sorted but the
  // sort bar never lit the matching button up.
  const toggleMemberFilter = (filter) => {
    setMemberFilter(
      applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy)
    );
  };

  useEffect(() => {
    const fetchMemorials = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setPayloadMemorials([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);

        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          setRosterMembers(extractAllMembersFromStatisticsResponse(response.data));
          const memorialsData =
            response?.data?.memorials_this_week_or_month?.details ||
            response?.data?.memorials_this_week_or_month ||
            [];

          if (Array.isArray(memorialsData)) {
            setPayloadMemorials(memorialsData);
          } else {
            console.warn("Memorials data is not an array:", memorialsData);
            setPayloadMemorials([]);
          }
        } else {
          setPayloadMemorials([]);
        }
      } catch (error) {
        console.error("Error fetching memorials:", error);
        setPayloadMemorials([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMemorials();
  }, []);

  // The months the pickers ask for, each with its rows. As on the anniversary
  // list, the payload's rows are keyed to the month each is dated in rather
  // than taken as the current month's, and each month is topped up from the
  // roster's deceased members for anyone of that month the payload left out.
  // The payload's rows carry the day already spelled out in their `date`
  // text; the roster's take it off the stored date — the rows print the same
  // either way.
  const rosterDeceased = useMemo(
    () => rosterMembers.filter(isDashboardUpcomingDeceasedMember),
    [rosterMembers]
  );
  const periodMonths = useMemo(() => {
    const monthIndexes =
      month === ALL_MONTHS
        ? MONTH_NAMES.map((_, index) => index)
        : [month - 1];

    return monthIndexes.map((monthIndex) => ({
      monthIndex,
      monthName: MONTH_NAMES[monthIndex],
      rows: mergeMonthMemorials(
        filterMemorialsByMonth(payloadMemorials, monthIndex),
        filterMemorialsByMonth(rosterDeceased, monthIndex)
      ),
    }));
  }, [month, payloadMemorials, rosterDeceased]);

  const memorials = useMemo(
    () => periodMonths.flatMap((group) => group.rows),
    [periodMonths]
  );

  const formatPersonName = (memorial) =>
    formatDashboardMemberDisplayName(memorial, {
      unnamedLabel: "",
      strictHeadForInLaw: true,
    });

  const getDeathYearNum = (memorial) => {
    const year = parseInt(getMemberDeathYear(memorial), 10);
    return Number.isNaN(year) ? -1 : year;
  };

  const extractDayNumber = (dateString) => {
    if (!dateString || typeof dateString !== "string") {
      return -1;
    }

    const monthNames = MONTH_NAMES;
    let formatted = dateString.trim();
    formatted = formatted.replace(/\s*-\s*Remembered/gi, "").trim();

    for (const month of monthNames) {
      if (formatted.startsWith(month + " ")) {
        formatted = formatted.substring(month.length + 1).trim();
        break;
      }
    }

    formatted = formatted.replace(/\s*\([^)]+\)/g, "").trim();

    const slashMatch = formatted.match(/^(\d{1,2})\/(\d{1,2})\/\d{4}$/);
    if (slashMatch) {
      return parseInt(slashMatch[2], 10);
    }

    const dayMatch = formatted.match(/^\d+/);
    if (dayMatch) {
      return parseInt(dayMatch[0], 10);
    }

    return -1;
  };

  const memorialDateString = (memorial) => {
    const d = memorial?.date;
    if (d && typeof d === "string") return d;
    if (memorial?.date_of_death) {
      try {
        return formatMsToMMDDYYYY(memorial.date_of_death);
      } catch {
        return "";
      }
    }
    return "";
  };

  const memorialDayForSort = (memorial) => {
    const fromStr = extractDayNumber(memorialDateString(memorial));
    if (fromStr !== -1) return fromStr;
    return getDashboardMemorialMonthDay(memorial)?.day ?? -1;
  };

  // The member tab narrows each month; the count reads the rows on screen.
  const filteredMonths = useMemo(
    () =>
      periodMonths.map((group) => ({
        ...group,
        rows: filterDashboardMembersByMemberFilter(group.rows, memberFilter),
      })),
    [periodMonths, memberFilter]
  );

  const filteredMemorials = useMemo(
    () => filteredMonths.flatMap((group) => group.rows),
    [filteredMonths]
  );

  const getMemberLineColor = (memorial) =>
    (memorial?.family_line_color || memorial?.color_name || "").toLowerCase();

  const getMemberLineColorLabel = (memorial) =>
    (memorial?.family_line_color || memorial?.color_name || "").trim() || "No Line";

  /**
   * Build display groups based on active sort, month by month — the month
   * grouping is the period's own shape and stands under every sort; only the
   * rows inside a month re-order. Date descending also reads the months last
   * to first, so a whole-year list runs December back to January.
   * - Date: group by day of month
   * - Line/Color: group by family line color
   * - Name / Year: one row per memorial (day badge kept), sorted globally
   */
  const sortedMonths = useMemo(() => {
    const sortMemorialRows = (monthRows) => {
    if (!monthRows.length) return [];

    const compareName = (a, b, descending = false) => {
      const cmp = formatPersonName(a)
        .toLowerCase()
        .localeCompare(formatPersonName(b).toLowerCase());
      return descending ? -cmp : cmp;
    };

    const compareDay = (a, b, descending = false) => {
      const dayA = memorialDayForSort(a);
      const dayB = memorialDayForSort(b);
      if (dayA === -1 && dayB === -1) return 0;
      if (dayA === -1) return 1;
      if (dayB === -1) return -1;
      return descending ? dayB - dayA : dayA - dayB;
    };

    const compareYear = (a, b, oldestFirst = true) => {
      const yearA = getDeathYearNum(a);
      const yearB = getDeathYearNum(b);
      if (yearA === -1 && yearB === -1) return 0;
      if (yearA === -1) return 1;
      if (yearB === -1) return -1;
      return oldestFirst ? yearA - yearB : yearB - yearA;
    };

    const compareColor = (a, b) => {
      const colorA = getMemberLineColor(a);
      const colorB = getMemberLineColor(b);
      if (colorA !== colorB) {
        return colorA.localeCompare(colorB);
      }
      return compareName(a, b) || compareDay(a, b);
    };

    const toIndividualDayRows = (list) =>
      list.map((memorial, index) => ({
        key: memorial?.id != null ? `m-${memorial.id}` : `m-idx-${index}`,
        day: memorialDayForSort(memorial),
        details: [memorial],
      }));

    const toDayGroups = (list, descending = false) => {
      const groupsByDay = new Map();
      list.forEach((memorial) => {
        const day = memorialDayForSort(memorial);
        if (day === -1) return;
        if (!groupsByDay.has(day)) {
          groupsByDay.set(day, []);
        }
        groupsByDay.get(day).push(memorial);
      });

      return Array.from(groupsByDay.entries())
        .map(([day, details]) => ({
          key: `day-${day}`,
          day: Number(day),
          details,
        }))
        .sort((a, b) => (descending ? b.day - a.day : a.day - b.day));
    };

    const toColorGroups = (list) => {
      const groupsByColor = new Map();
      list.forEach((memorial) => {
        const label = getMemberLineColorLabel(memorial);
        if (!groupsByColor.has(label)) {
          groupsByColor.set(label, []);
        }
        groupsByColor.get(label).push(memorial);
      });

      return Array.from(groupsByColor.entries())
        .sort(([colorA], [colorB]) =>
          colorA.toLowerCase().localeCompare(colorB.toLowerCase())
        )
        .map(([colorLabel, details]) => ({
          key: `color-${colorLabel}`,
          day: null,
          colorLabel,
          details: [...details].sort(
            (a, b) => compareDay(a, b) || compareName(a, b)
          ),
        }));
    };

    let sortedMembers = [...monthRows];

    if (sortBy === "death-asc") {
      sortedMembers = sortDashboardMembersByDeathDate(sortedMembers);
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "alpha-asc") {
      sortedMembers.sort((a, b) => compareName(a, b));
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "alpha-desc") {
      sortedMembers.sort((a, b) => compareName(a, b, true));
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "oldest") {
      sortedMembers.sort((a, b) => compareYear(a, b, true));
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "youngest") {
      sortedMembers.sort((a, b) => compareYear(a, b, false));
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "year-asc") {
      sortedMembers.sort((a, b) => compareYear(a, b, true) || compareName(a, b));
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "year-desc") {
      sortedMembers.sort((a, b) => compareYear(a, b, false) || compareName(a, b));
      return toIndividualDayRows(sortedMembers);
    }

    if (sortBy === "color") {
      sortedMembers.sort(compareColor);
      return toColorGroups(sortedMembers);
    }

    // date-asc (default), date-desc, none — group by calendar day
    const descending = sortBy === "date-desc";
    sortedMembers.sort(
      (a, b) => compareDay(a, b, descending) || compareName(a, b)
    );
    return toDayGroups(sortedMembers, descending);
    };

    const months = filteredMonths
      .filter((group) => group.rows.length > 0)
      .map((group) => ({ ...group, groups: sortMemorialRows(group.rows) }));

    return sortBy === "date-desc" ? months.reverse() : months;
  }, [filteredMonths, sortBy]);

  /** The day groups of one month, flattened to the rows its grid prints. */
  const toMemorialGridRows = (sortedGroupedMemorials) => {
    const rows = [];

    sortedGroupedMemorials.forEach((group) => {
      if (group.colorLabel) {
        // Rows still group by line colour under this sort, but the group carries no
        // title — every row already names its line in the colour column.
        (group.details || []).forEach((memorial, index) => {
          rows.push({
            type: "memorial",
            key: `${group.key}-${memorial?.id || index}`,
            memorial,
            day: memorialDayForSort(memorial),
            showBadge: true,
          });
        });
        return;
      }

      const day = group.day;
      (group.details || []).forEach((memorial, index) => {
        rows.push({
          type: "memorial",
          key: `${group.key || `day-${day}`}-${memorial?.id || index}`,
          memorial,
          day,
          showBadge: index === 0,
        });
      });
    });

    return rows;
  };

  const renderMemorialGridRow = (memorial, { day, showBadge, rowKey }) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(memorial, {
      unnamedLabel: "",
      strictHeadForInLaw: true,
    });
    const isDeceased = memorial?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(memorial);
    const showInLawAsterisk =
      isInLawMember(memorial) && !isExplicitDashboardStartOrTopOfLineMember(memorial);
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(memorial, headsMap);
    const hexCode = memorial?.family_line_hex_code || memorial?.hex_code || "#000000";
    const colorName = memorial?.family_line_color || memorial?.color_name || "";
    const iconSize = isLargeView ? "18px" : "12px";
    const dayLabel = day !== -1 && day != null ? day : "—";
    const yearsSinceDeath = getMemberTimeSinceDeathLabel(memorial);
    const angel = (
      <span className={`dashboard-detail-angel-icon ${isLargeView ? "large-view" : ""}`}>
        <img
          src={window.getPublicUrl("/assets/images/angel.png")}
          alt="deceased"
          style={{ width: iconSize, height: iconSize }}
        />
      </span>
    );
    // Every name carries its own angel. "Mayumi Smith*👼 (Terry)": the in-law's
    // angel sits on their name, after their asterisk; when Terry has passed too
    // Terry gets one after that name as well: "Oscar Plant*👼 (Gwendolyn)👼".
    const angelAfterBaseName = isDeceased && Boolean(descendantSpouseName);
    const trailingAngel = descendantSpouseName
      ? isDashboardDescendantSpouseDeceased(memorial, rosterMembers)
      : isDeceased;
    const asteriskAfterBaseName = Boolean(descendantSpouseName);
    const inLawAsterisk = showInLawAsterisk ? (
      <DashboardInLawAsterisk isLargeView={isLargeView} />
    ) : null;

    return (
      <div key={rowKey} className="memorial-grid-row">
        {showBadge ? (
          <span className="ages-milestone-badge memorial-grid-badge">{dayLabel}</span>
        ) : (
          <span className="ages-milestone-spacer-badge memorial-grid-badge" aria-hidden="true">
            {dayLabel}
          </span>
        )}
        <div className={`memorial-grid-name ${isLargeView ? "large-view" : ""}`}>
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(memorial)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={baseName}
                descendantSpouseName={descendantSpouseName}
                afterBaseName={
                  asteriskAfterBaseName || angelAfterBaseName ? (
                    <>
                      {asteriskAfterBaseName && inLawAsterisk}
                      {angelAfterBaseName && angel}
                    </>
                  ) : null
                }
              />
            </DashboardPhotoName>
            {/* Markers run in the same order as the footer legend reads them:
                *In-Laws, then Deceased, then Multiple Births. */}
            {!asteriskAfterBaseName && inLawAsterisk}
            {trailingAngel && angel}
            {multipleBirthSup && (
              <sup className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}>
                {multipleBirthSup}
              </sup>
            )}
          </DashboardNameMarkerGroup>
        </div>
        <div className={`memorial-grid-year ${isLargeView ? "large-view" : ""}`}>
          {getMemberDeathYear(memorial)}
          {/* Years deceased, next to the year it is counted from */}
          {yearsSinceDeath != null && (
            <span className="memorial-grid-years-since">({yearsSinceDeath})</span>
          )}
        </div>
        {/* Head of Line Name and Color — compact prints the line colour in its own
            colour, and the footer legend ties each colour to its head */}
        {isCompactLayout ? (
          <div className="memorial-grid-line dashboard-detail-line-col--compact">
            <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
          </div>
        ) : (
          <div className={`memorial-grid-line ${isLargeView ? "large-view" : ""}`}>
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(memorial, "head_image")}
              onViewPhoto={openPhoto}
              member={memorial}
              headsMap={headsMap}
            />
          </div>
        )}
      </div>
    );
  };

  const renderDirectionalSortButton = (label, ascKey, descKey, title) => (
    <button
      type="button"
      onClick={() => {
        if (sortBy === ascKey) {
          setSortBy(descKey);
        } else {
          setSortBy(ascKey);
        }
      }}
      className={`ages-milestone-sort-btn ${
        sortBy === ascKey || sortBy === descKey ? "active" : ""
      }`}
      title={title}
    >
      {label}
      {sortBy === ascKey ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: "4px" }}
        >
          <path d="M18 15l-6-6-6 6" />
        </svg>
      ) : sortBy === descKey ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: "4px" }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      ) : (
        <span
          style={{
            marginLeft: "4px",
            display: "inline-flex",
            flexDirection: "column",
            lineHeight: 1,
            alignItems: "center",
          }}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block" }}
          >
            <path d="M18 15l-6-6-6 6" />
          </svg>
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block", marginTop: "-2px" }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      )}
    </button>
  );

  const leadingSortButtons = (
    <>
      <DashboardPeriodPickers
        month={month}
        onMonthChange={setMonth}
        year={year}
        onYearChange={setYear}
      />
      {renderDirectionalSortButton("Date", "date-asc", "date-desc", "Sort by Date")}
      {renderDirectionalSortButton("Year", "year-asc", "year-desc", "Sort by Year")}
    </>
  );

  // The current month keeps the full "Memoriams this Month" heading; any other
  // month or year reads "this Month" wrong, so the shorter title carries it
  // there.
  const isCurrentMonthOfCurrentYear =
    month === now.getMonth() + 1 && year === now.getFullYear();
  const listTitle = isCurrentMonthOfCurrentYear
    ? "Memoriams this Month"
    : "Memoriams";

  // Built from the whole tree, not this page's rows, so the footer legend lists the
  // same family lines here as on every other dashboard list. Built from the rows it
  // only showed lines this page happened to contain.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(rosterMembers, headsMap),
    [rosterMembers, headsMap]
  );

  const showColorLegend = isCompactLayout && legendLines.length > 0;

  const legendFooter = (
    <div className="memorials-this-week-page in-law-footer">
      {/* filteredMemorials, not the day groups built from it — same rows, one list */}
      <DashboardMarkerLegend
        multipleBirthTags={getMultipleBirthLegendTags(filteredMemorials)}
      />
      {showColorLegend && (
        <DashboardColorLegend lines={legendLines} />
      )}
    </div>
  );

  return (
    <div className={`dashboard-container ages-milestone-container memorials-this-week-page dashboard-detail-page ${isLargeView ? "large-view-mode" : ""}`}>
      <div className="dash-box ages-milestone-box dashboard-detail-box">
        <div className="ages-milestone-header">
          <div className="ages-milestone-header-left">
            <DashboardBackButton />
            <h3 className="ages-milestone-title">{listTitle}</h3>
            <div className="ages-milestone-count">
              {filteredMemorials.length}
            </div>
            {/* Up whenever the list has loaded, empty month included — the
                pickers on it are how the reader moves to another month. */}
            {!loading && (
              <DashboardDetailSortFilterBar
                className="ages-milestone-sort-toggle"
                buttonClassName="ages-milestone-sort-btn"
                sortBy={sortBy}
                onSortChange={setSortBy}
                memberFilter={memberFilter}
                onMemberFilterToggle={toggleMemberFilter}
                excludeDeceasedFilters={true}
                showAgeSorts={false}
                // An In Memoriam list holds no living members, so the living
                // descendants tab would only ever come back empty here.
                excludedMemberFilters={[
                  "start-top",
                  "in-relationship",
                  "descendants-living",
                ]}
                inLawsFilterKey="in-laws-deceased"
                inLawsLabel="In-Laws"
                leadingSortButtons={leadingSortButtons}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="ages-milestone-empty-state">
            <p>Loading memorials...</p>
          </div>
        ) : memorials.length === 0 ? (
          <div className="ages-milestone-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : (
          <div className="memorials-this-week-page dashboard-detail-content-wrapper">
            {filteredMemorials.length === 0 ? (
              <div className="ages-milestone-empty-state">
                <p>No data for this period.</p>
              </div>
            ) : (
              <div className="memorials-list-cluster">
                {/* One grid for every month, so the columns line up down the
                    whole list and the cluster stays the page's one scroller;
                    each month's heading is a row spanning it. */}
                <div
                  className={`ages-milestone-list memorials-list-grid ${
                    isCompactLayout ? "compact" : ""
                  }`}
                >
                  {sortedMonths.map((monthGroup) => (
                    <React.Fragment key={monthGroup.monthIndex}>
                      <div className={`ages-milestone-month-header memorials-grid-month-header ${isLargeView ? "large-view" : ""}`}>
                        <span className="ages-milestone-month-name dashboard-section-heading">
                          {monthGroup.monthName}
                          {/* With All Months picked each heading carries its own count; a single month already shows it at the top. */}
                          {month === ALL_MONTHS && (
                            <span className="dashboard-section-heading-count">{` (${monthGroup.rows.length})`}</span>
                          )}
                        </span>
                      </div>
                      {toMemorialGridRows(monthGroup.groups).map((row) => {
                        return renderMemorialGridRow(row.memorial, {
                          day: row.day,
                          showBadge: row.showBadge,
                          rowKey: row.key,
                        });
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {legendFooter}
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default MemorialsThisWeek;
